import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../components/common/Modal';
import { WorkOrderRoastViewer } from '../../components/workOrder/WorkOrderRoastViewer';
import { workOrderService } from '../../services/workOrderService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useLanguage } from '../../features/language/useLanguage';

/** Scan target for a Work Order QR label: opens the read-only roast viewer as a modal. */
export const WorkOrderViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const { data: workOrder, isLoading } = useQuery({
    queryKey: ['work-order', id],
    queryFn: () => workOrderService.getById(id as string),
    enabled: !!id,
  });

  const close = () => navigate('/work-orders');

  return (
    <Modal
      isOpen
      onClose={close}
      title={workOrder?.workOrderNumber || t('workOrders.page.viewWorkOrder')}
      maxWidth="7xl"
    >
      {/* A printed QR label can outlive its work order, so a scanned id may be
          missing/deleted. When the fetch settles with no work order, show a
          message instead of spinning forever; the modal's close button
          navigates back to the work orders list. */}
      {isLoading ? (
        <LoadingSpinner />
      ) : workOrder ? (
        <WorkOrderRoastViewer workOrder={workOrder} />
      ) : (
        <p className="p-8 text-center text-sm text-gray-500" data-test-id="work-order-view-error">
          {t('workOrders.page.errors.loadDetailsFailed')}
        </p>
      )}
    </Modal>
  );
};
